import toast from 'react-hot-toast';
import PushNotificationToast from '../components/PushNotificationToast';
import {
  FCM_SW_SHOW_NOTIFICATION,
  parseFcmPushPayload,
  resolvePushNotificationTag,
} from '../utils/fcmPushPayload';
import { playPushAlertEffects } from '../utils/pushAlertEffects';
import { getMessagingServiceWorkerRegistration } from './fcmWebRegistration';
import {
  buildPushNotificationBody,
  getPushNotificationTimestamp,
} from '../utils/pushNotificationPresentation';

const PUSH_TOAST_DURATION_MS = 10000;
const PUSH_DEDUPE_WINDOW_MS = 5000;
const recentPushDeliveryKeys = new Map();

const getPushDeliveryKey = (payload = {}) => {
  const { notificationId, data } = parseFcmPushPayload(payload);
  return notificationId || data?.messageId || data?.id || payload?.messageId || null;
};

const shouldSkipDuplicatePushDelivery = (payload = {}) => {
  const key = getPushDeliveryKey(payload);
  if (!key) {
    return false;
  }

  const now = Date.now();
  const lastSeenAt = recentPushDeliveryKeys.get(key);
  if (lastSeenAt != null && now - lastSeenAt < PUSH_DEDUPE_WINDOW_MS) {
    return true;
  }

  recentPushDeliveryKeys.set(key, now);
  if (recentPushDeliveryKeys.size > 100) {
    const cutoff = now - PUSH_DEDUPE_WINDOW_MS;
    for (const [storedKey, seenAt] of recentPushDeliveryKeys) {
      if (seenAt < cutoff) {
        recentPushDeliveryKeys.delete(storedKey);
      }
    }
  }

  return false;
};

/** Set from useFcmPushMessageListener so window.Notification clicks can navigate + mark read. */
let windowNotificationClickHandler = null;

export const setWindowNotificationClickHandler = (handler) => {
  windowNotificationClickHandler = handler;
};

const notifyWindowNotificationClick = (data = {}) => {
  if (typeof windowNotificationClickHandler === 'function') {
    windowNotificationClickHandler({ data });
  }
};

const getDefaultNotificationIcon = () => {
  if (typeof window === 'undefined') {
    return undefined;
  }
  return `${window.location.origin}/favicon.ico`;
};

const toNotificationDataRecord = (data = {}) =>
  Object.fromEntries(
    Object.entries(data).map(([key, value]) => [key, value == null ? '' : String(value)]),
  );

const buildNotificationOptions = (payload) => {
  const { title, data, type, eventId, notificationId } = parseFcmPushPayload(payload);
  const displayBody = buildPushNotificationBody(data, title);
  const notificationData = toNotificationDataRecord({
    ...data,
    type: type || data.type,
    eventId: eventId || data.eventId,
    event_id: eventId || data.event_id,
    targetId: data.targetId || eventId || '',
    targetType:
      data.targetType || (type && type.startsWith('partner_group_') ? type : '') || 'partner_event',
    id: notificationId || data.id || data._id || data.notificationId || data.notification_id || '',
    notificationId:
      notificationId || data.notificationId || data.notification_id || data.id || data._id || '',
  });
  return {
    body: displayBody || undefined,
    icon: getDefaultNotificationIcon(),
    timestamp: getPushNotificationTimestamp(data),
    data: notificationData,
    tag: resolvePushNotificationTag(notificationData),
    silent: true,
    // ALERT_SOUND_DISABLED — OS notification sound: silent: false,
  };
};

const isFirebaseMessagingWorker = (worker) =>
  Boolean(worker?.scriptURL?.includes('firebase-messaging-sw.js'));

const showViaServiceWorker = async (title, options) => {
  const registration = await getMessagingServiceWorkerRegistration();
  if (!registration) {
    return false;
  }

  try {
    await registration.showNotification(title, options);
    return true;
  } catch (error) {
    console.warn('[FCM] registration.showNotification failed:', error);
  }

  const worker = registration.active;
  if (isFirebaseMessagingWorker(worker)) {
    worker.postMessage({
      type: FCM_SW_SHOW_NOTIFICATION,
      title,
      options,
    });
    return true;
  }

  return false;
};

const showViaWindowNotification = (title, options) => {
  try {
    const notification = new Notification(title, options);
    notification.onclick = () => {
      window.focus();
      notification.close();
      notifyWindowNotificationClick(options?.data ?? {});
    };
    return true;
  } catch (error) {
    console.warn('[FCM] window Notification failed:', error);
    return false;
  }
};

/** OS / browser notification via the FCM service worker (works in foreground on Safari). */
export const showBrowserSystemNotification = async (payload) => {
  if (typeof window === 'undefined' || typeof Notification === 'undefined') {
    return false;
  }

  if (Notification.permission !== 'granted') {
    return false;
  }

  const { title } = parseFcmPushPayload(payload);
  const options = buildNotificationOptions(payload);
  const withoutIcon = { ...options, icon: undefined };

  let shown = false;

  if (await showViaServiceWorker(title, options)) {
    shown = true;
  } else if (await showViaServiceWorker(title, withoutIcon)) {
    shown = true;
  } else if (showViaWindowNotification(title, options)) {
    shown = true;
  } else if (showViaWindowNotification(title, withoutIcon)) {
    shown = true;
  }

  if (shown) {
    playPushAlertEffects(payload);
  }

  return shown;
};

export const showForegroundPushToast = (payload, { onOpen }) => {
  const { title } = parseFcmPushPayload(payload);

  return toast.custom(
    (t) => (
      <PushNotificationToast
        toastId={t.id}
        title={title}
        payload={payload}
        onOpen={onOpen}
        onDismiss={(id) => toast.dismiss(id)}
      />
    ),
    {
      duration: PUSH_TOAST_DURATION_MS,
      position: 'top-right',
      id: `push-${resolvePushNotificationTag(payload?.data ?? {})}`,
    },
  );
};

/**
 * Foreground (tab visible): in-app toast only.
 * Hidden tab: browser notification (no toast).
 * https://firebase.google.com/docs/cloud-messaging/web/receive-messages
 */
export const isPortalTabVisible = () =>
  typeof document !== 'undefined' && document.visibilityState === 'visible';

export const handleForegroundPushMessage = async (payload, { onOpen, onNewNotification } = {}) => {
  if (shouldSkipDuplicatePushDelivery(payload)) {
    return;
  }

  onNewNotification?.(payload);

  if (isPortalTabVisible()) {
    showForegroundPushToast(payload, { onOpen });
    playPushAlertEffects(payload);
    return;
  }

  const browserShown = await showBrowserSystemNotification(payload);
  if (!browserShown) {
    playPushAlertEffects(payload);
  }
};

export const handlePushNotificationNavigation = (message = {}, { openNotification } = {}) => {
  if (!openNotification) {
    return;
  }

  const rawData = message?.data ?? message?.payload?.data ?? {};
  const source =
    rawData && typeof rawData === 'object' && Object.keys(rawData).length > 0 ? rawData : message;

  void openNotification(source);
};

/** Query params used when the SW opens a new window (no existing portal tab). */
export const PUSH_CLICK_QUERY_FLAG = 'push';

export const readPushNotificationClickFromUrl = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const params = new URLSearchParams(window.location.search);
  if (params.get(PUSH_CLICK_QUERY_FLAG) !== '1') {
    return null;
  }

  const data = {};
  params.forEach((value, key) => {
    if (key === PUSH_CLICK_QUERY_FLAG) {
      return;
    }
    data[key] = value;
  });

  return Object.keys(data).length > 0 ? data : null;
};

export const clearPushNotificationClickFromUrl = () => {
  if (typeof window === 'undefined') {
    return;
  }

  const url = new URL(window.location.href);
  if (!url.searchParams.has(PUSH_CLICK_QUERY_FLAG)) {
    return;
  }

  url.searchParams.delete(PUSH_CLICK_QUERY_FLAG);
  [
    'id',
    'notificationId',
    'notification_id',
    'type',
    'notificationType',
    'eventId',
    'event_id',
    'targetId',
    'targetType',
    'path',
    'url',
    'link',
    'role',
    'userRole',
    'screen',
    'tab',
  ].forEach((key) => url.searchParams.delete(key));

  const next = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState({}, '', next || url.pathname);
};
